import {useCallback, useMemo} from "react";
import {useParams} from "react-router-dom";

const storage = localStorage;

const getStorageValue = (key, fallback = null) => {
    try {
        const value = storage.getItem(key);
        return value ? JSON.parse(value) : fallback;
    } catch {
        return fallback;
    }
};

const setStorageValue = (key, value) => {
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch (e) {
        console.error("Storage error:", e);
    }
};

export const useApplicationStorage = () => {
    const {applicationId} = useParams();

    const keys = useMemo(() => ({
        lead: applicationId ? `lead_${applicationId}` : "lead",
        students: applicationId ? `students_${applicationId}` : "students",
    }), [applicationId]);

    const leadData = useMemo(() => getStorageValue(keys.lead), [keys.lead]);
    const studentsData = useMemo(() => getStorageValue(keys.students, []), [keys.students]);

    const getLead = useCallback(() => {
        return getStorageValue(keys.lead, null);
    }, [keys.lead]);

    const setLead = useCallback((lead, key) => {
        setStorageValue(key || keys.lead, lead);
    }, [keys.lead]);

    const getStudents = useCallback(() => {
        return getStorageValue(keys.students, []);
    }, [keys.students]);

    const setStudents = useCallback((students) => {
        setStorageValue(keys.students, students);
    }, [keys.students]);

    const updateStudent = useCallback((childId, data) => {
        const students = getStorageValue(keys.students, []);
        const index = students.findIndex((student) => student.childId === childId);

        if (index !== -1) {
            students[index] = {...students[index], ...data};
        } else {
            students.push({...data, childId});
        }

        setStorageValue(keys.students, students);
    }, [keys.students]);

    const removeStudent = useCallback((childId) => {
        const students = getStorageValue(keys.students, []);
        const filtered = students.filter((student) => student.childId !== childId);
        setStorageValue(keys.students, filtered);
    }, [keys.students]);

    const clear = useCallback(() => {
        storage.removeItem(keys.lead);
        storage.removeItem(keys.students);
    }, [keys]);

    return {
        leadData,
        studentsData,
        getLead,
        setLead,
        getStudents,
        setStudents,
        updateStudent,
        removeStudent,
        clear,
    };
};
